import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import PasswordInput from '@/components/PasswordInput';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, Chrome } from 'lucide-react';
import { api, GOOGLE_LOGIN_URL } from '@/utils/api';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Only show the Google button if the server actually has it configured.
  useEffect(() => {
    api.googleEnabled()
      .then((res) => setGoogleEnabled(res.data.enabled))
      .catch(() => setGoogleEnabled(false));
  }, []);

  // A failed Google sign-in redirects back here as /login?error=...
  useEffect(() => {
    const error = searchParams.get('error');
    if (error) toast.error(error);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Login successful');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-brand relative items-center justify-center p-16">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(circle at 20% 15%, rgba(224,122,95,0.18), transparent 45%), radial-gradient(circle at 85% 80%, rgba(255,255,255,0.10), transparent 40%)' }} />
        <div className="relative z-10 max-w-md">
          <img
            src="/wildnet-logo-light.webp"
            alt="Wildnet Technologies"
            className="h-10 w-auto mb-8"
          />
          <h1 className="text-4xl font-light text-white tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Candidate Assessment Portal
          </h1>
          <p className="text-white/60 text-base leading-relaxed">
            AI-powered personality assessments for smarter hiring decisions. Identify the right talent with data-driven insights.
          </p>
          <div className="mt-12 flex gap-8">
            <div>
              <p className="text-3xl font-light text-accent2">84</p>
              <p className="text-xs text-white/50 uppercase tracking-wider mt-1">Questions</p>
            </div>
            <div>
              <p className="text-3xl font-light text-accent2">7</p>
              <p className="text-xs text-white/50 uppercase tracking-wider mt-1">Dimensions</p>
            </div>
            <div>
              <p className="text-3xl font-light text-accent2">AI</p>
              <p className="text-xs text-white/50 uppercase tracking-wider mt-1">Analysis</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center bg-[#F9F9F6] p-8">
        <div className="w-full max-w-sm">
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-6">
              <img src="/wildnet-logo.png" alt="Wildnet Technologies" className="h-8 w-auto" />
            </div>
            <h2 className="text-2xl font-medium text-[#1A231C]" style={{ fontFamily: 'Outfit, sans-serif' }}>Sign in</h2>
            <p className="text-sm text-[#5C665F] mt-1">Access the admin dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-[0.1em] uppercase font-bold text-[#5C665F]">Email</Label>
              <Input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="bg-transparent border-0 border-b-2 border-black/10 focus:border-brand focus:ring-0 rounded-none px-0 py-3 placeholder:text-black/30"
                placeholder="admin@company.com"
                data-testid="email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-[0.1em] uppercase font-bold text-[#5C665F]">Password</Label>
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required
                className="bg-transparent border-0 border-b-2 border-black/10 focus:border-brand focus:ring-0 rounded-none px-0 py-3 placeholder:text-black/30"
                placeholder="Enter password"
                data-testid="password-input"
              />
            </div>
            <button
              type="submit" disabled={loading}
              className="w-full bg-brand text-white hover:bg-brand-dark transition-colors rounded-none px-6 py-3.5 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="login-submit-button"
            >
              <Lock className="h-4 w-4" />
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 border-t border-black/10" />
            <span className="text-xs text-[#5C665F] uppercase tracking-wider">or</span>
            <div className="flex-1 border-t border-black/10" />
          </div>

          {googleEnabled && (
          <a
            href={GOOGLE_LOGIN_URL}
            className="w-full bg-white border-2 border-black/10 hover:border-black/20 transition-colors rounded-none px-6 py-3.5 font-medium flex items-center justify-center gap-3 text-[#1A231C]"
            data-testid="google-sign-in-button"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
              <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 2.58 9 2.58z" fill="#EA4335"/>
            </svg>
            Sign in with Google
          </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
